import React from 'react'
import { Formik } from 'formik'
import { useHistory } from 'react-router-dom'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import { Button } from '@mui/material'
import { type TwoFactorLoginPayload } from '../../../api-schema-types/user/AuthPayloadDtos'
import { type LoginResponse } from '../../../api-schema-types/user/AuthDomainDtos'
import { useAuthLoginTwoFactorMutation } from '../../../api-services/user/authenticationApi'
import { AuthValidations } from '../../../validations/AuthValidations'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { setNotification } from '../ui-widgets/uiWidgetsSlice'
import { useAppDispatch } from '../../../app/hooks'
import { AuthService } from '../../../services/AuthService'
import FormikTextField from '../ui-widgets/FormikTextField'
import ErrorComponent from '../ui-widgets/ErrorComponent'
import ButtonLoadingComponent from '../../../views/components/shared/ButtonLoadingComponent'

interface TwoFactorChallengeComponentProps {
  challengeToken: string
  onCancel: () => void
}

const TwoFactorChallengeComponent: React.FC<TwoFactorChallengeComponentProps> = (props: TwoFactorChallengeComponentProps) => {
  const { challengeToken, onCancel } = props

  const dispatch = useAppDispatch()
  const history = useHistory()

  const goTo = (location: string): void => {
    history.push(location)
  }

  const [apiLoginTwoFactor, { isLoading }] = useAuthLoginTwoFactorMutation()

  const handleVerify = (payload: TwoFactorLoginPayload): void => {
    apiLoginTwoFactor(payload)
      .unwrap()
      .then(async (response: LoginResponse) => {
        await AuthService.setLoginToken(response.accessToken)
        goTo('/app/dashboard')
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  const defaultForm: TwoFactorLoginPayload = {
    challengeToken,
    otp: ''
  }

  return (
    <Paper sx={{ p: 4 }}>
      <Typography variant="h5">Two-Factor Authentication</Typography>
      <Typography sx={{ mt: 1, mb: 3 }}>
        Enter the verification code displayed in your authenticator app to finish signing in.
      </Typography>

      <Formik
        initialValues={defaultForm}
        validationSchema={AuthValidations.twoFactorLoginValidation}
        onSubmit={async (values, actions) => {
          handleVerify(values)
        }}
      >
        {formikProps => (
          <form
            autoComplete={'off'}
            autoCorrect={'off'}
            onSubmit={formikProps.handleSubmit}
          >
            <Grid container spacing={2}>
              {!formikProps.isValid && (
                <Grid item md={12} sx={{ mt: 3 }}>
                  <ErrorComponent title={'Form validation error'} message={Object.values(formikProps.errors).join(', ')}/>
                </Grid>
              )}
              <Grid item md={12}>
                <FormikTextField
                  formikProps={formikProps}
                  fieldName={'otp'}
                  label={'Verification Code'}
                  type={'text'}
                  locked={false}
                />
              </Grid>
              <Grid item md={12}>
                <Button
                  variant={'outlined'}
                  type="submit"
                  sx={{ mr: 2 }}
                >
                  Verify
                  {isLoading && <ButtonLoadingComponent/>}
                </Button>
                <Button
                  variant={'text'}
                  type="button"
                  disabled={isLoading}
                  onClick={() => {
                    onCancel()
                  }}
                >
                  Back to sign-in
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </Paper>
  )
}

export default TwoFactorChallengeComponent
